import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-5 bg-fleet-bg">
            {/* Left visual */}
            <div className="hidden lg:flex lg:col-span-3 relative bg-fleet-ink text-white p-12 flex-col justify-between overflow-hidden">
                <div className="relative z-10">
                    <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-sm bg-white flex items-center justify-center">
                            <span className="font-display font-bold text-fleet-ink">E</span>
                        </div>
                        <div className="font-display font-semibold text-lg tracking-tight">EAZYFLEET</div>
                    </div>
                </div>

                <div className="relative z-10 max-w-xl">
                    <div className="label-mono text-white/60 mb-4">FLEET OPERATIONS PLATFORM</div>
                    <h1 className="font-display font-semibold text-4xl xl:text-5xl leading-tight tracking-tight">
                        The control room for your entire fleet.
                    </h1>
                    <p className="mt-5 text-white/70 text-base leading-relaxed max-w-md">
                        Vehicles, work orders, spares, tyres, batteries, consumables — one
                        operational system that works the way your workshop already does.
                    </p>

                    <div className="mt-10 grid grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden">
                        {[
                            { k: "VEHICLES", v: "Real-time" },
                            { k: "WORK ORDERS", v: "Kanban" },
                            { k: "TYRES", v: "Position-wise" },
                        ].map((m) => (
                            <div key={m.k} className="bg-fleet-ink p-4">
                                <div className="label-mono text-white/50">{m.k}</div>
                                <div className="font-display text-lg mt-1">{m.v}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative z-10 label-mono text-white/40">
                    v 1.0.1
                </div>

                {/* Decorative grid */}
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                        backgroundSize: "48px 48px",
                    }}
                />
            </div>

            {/* Right form */}
            <div className="lg:col-span-2 flex items-center justify-center p-6">
                <div className="w-full max-w-sm">
                    <div className="lg:hidden mb-8 flex items-center gap-2">
                        <div className="h-8 w-8 rounded-sm bg-fleet-ink flex items-center justify-center">
                            <span className="font-display font-bold text-white">E</span>
                        </div>
                        <div className="font-display font-semibold text-lg">EAZYFLEET</div>
                    </div>

                    <div className="label-mono mb-2">SIGN IN</div>
                    <h2 className="font-display text-2xl font-semibold mb-1">Welcome back</h2>
                    <p className="text-sm text-fleet-inkSoft mb-8">
                        Sign in to access your fleet operations dashboard.
                    </p>

                    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="label-mono block mb-1.5">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-3 py-2 bg-white border border-fleet-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fleet-ink focus:border-fleet-ink"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <label className="label-mono block mb-1.5">Password</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full px-3 py-2 pr-10 bg-white border border-fleet-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fleet-ink focus:border-fleet-ink"
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-fleet-inkSoft hover:text-fleet-ink transition-colors"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    data-testid="toggle-password-visibility"
                                >
                                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-fleet-critical bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full mt-2 bg-fleet-ink hover:bg-slate-800 text-white text-sm font-medium py-2.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
                            data-testid="login-submit"
                        >
                            {loading ? "Signing in…" : (<><span>Sign in</span><ArrowRight size={16} /></>)}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
